import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ROLE_LABELS = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Administrador',
  OPERATOR: 'Operador',
  CUSTOMER_USER: 'Usuario',
};

const ROLE_COLORS = {
  SUPER_ADMIN: { bg: '#008B9A', text: '#FFFFFF' },
  ADMIN: { bg: '#00BCD4', text: '#FFFFFF' },
  OPERATOR: { bg: '#D97706', text: '#FFFFFF' },
  CUSTOMER_USER: { bg: '#0ABFCC', text: '#FFFFFF' },
};

const UserContextIndicator = ({
  user = { name: 'Carlos Rodríguez', role: 'ADMIN', organization: 'TechCorp S.A.S.' },
  isCollapsed = false,
  onLogout,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const initials = user?.name
    ? user?.name?.split(' ')?.map((n) => n?.[0])?.slice(0, 2)?.join('')?.toUpperCase()
    : 'U';

  const roleLabel = ROLE_LABELS?.[user?.role] || user?.role;
  const roleColor = ROLE_COLORS?.[user?.role] || ROLE_COLORS?.CUSTOMER_USER;

  const handleToggleMenu = () => setShowMenu((prev) => !prev);

  const handleLogout = () => {
    setShowMenu(false);
    if (onLogout) onLogout();
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' || e?.key === ' ') {
      e?.preventDefault();
      handleToggleMenu();
    }
    if (e?.key === 'Escape') {
      setShowMenu(false);
    }
  };

  return (
    <div className="relative">
      {showMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowMenu(false)}
          aria-hidden="true"
        />
      )}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Usuario: ${user?.name}, Rol: ${roleLabel}`}
        aria-expanded={showMenu}
        aria-haspopup="menu"
        className="user-context"
        onClick={handleToggleMenu}
        onKeyDown={handleKeyDown}
      >
        <div
          className="user-avatar"
          style={{ backgroundColor: roleColor?.bg, color: roleColor?.text }}
          aria-hidden="true"
        >
          {initials}
        </div>

        <div className="user-info">
          <div className="user-name" title={user?.name}>
            {user?.name}
          </div>
          <div
            className="user-role-badge"
            style={{
              backgroundColor: `${roleColor?.bg}20`,
              color: roleColor?.bg,
            }}
            title={user?.organization}
          >
            {roleLabel}
          </div>
        </div>

        {!isCollapsed && (
          <Icon
            name={showMenu ? 'ChevronUp' : 'ChevronDown'}
            size={14}
            color="var(--color-muted-foreground)"
            aria-hidden="true"
          />
        )}
      </div>
      {/* Dropdown menu */}
      {showMenu && (
        <div
          role="menu"
          aria-label="Opciones de usuario"
          className="absolute bottom-full left-0 right-0 mb-2 rounded-md border"
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 'var(--z-dropdown)',
            minWidth: isCollapsed ? '180px' : 'auto',
            left: isCollapsed ? 'calc(100% + 8px)' : '0',
            bottom: isCollapsed ? '0' : 'calc(100% + 4px)',
          }}
        >
          <div
            className="px-3 py-2 border-b"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <p
              className="text-sm font-medium truncate"
              style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}
            >
              {user?.name}
            </p>
            {user?.organization && (
              <p
                className="text-xs truncate mt-0.5"
                style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}
              >
                {user?.organization}
              </p>
            )}
          </div>

          <div className="p-1">
            <button
              role="menuitem"
              className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition-base hover:bg-muted text-left"
              style={{
                fontFamily: 'var(--font-caption)',
                color: 'var(--color-foreground)',
                transition: 'all var(--transition-base)',
                borderRadius: 'var(--radius-sm)',
              }}
              onClick={() => setShowMenu(false)}
            >
              <Icon name="User" size={14} color="currentColor" />
              Mi Perfil
            </button>

            <button
              role="menuitem"
              className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-left"
              style={{
                fontFamily: 'var(--font-caption)',
                color: 'var(--color-error)',
                transition: 'all var(--transition-base)',
                borderRadius: 'var(--radius-sm)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-muted)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={handleLogout}
            >
              <Icon name="LogOut" size={14} color="var(--color-error)" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserContextIndicator;